"use client";

import { SerwistProvider } from "@serwist/turbopack/react";
import { type ReactNode, useEffect, useState } from "react";

/**
 * Registers the PWA worker after first paint so it doesn't contend with LCP.
 * Children render immediately; the provider is a sibling (nothing in the tree
 * reads `useSerwist`), so flipping it on does not remount the page.
 */
export function DeferredSerwist({
  swUrl,
  children,
}: {
  swUrl: string;
  children: ReactNode;
}) {
  const [register, setRegister] = useState(false);

  useEffect(() => {
    // Never register in dev — a cached service worker survives normal reloads (and
    // even most hard refreshes) and silently keeps serving a stale JS bundle while
    // you iterate, which reads as "the fix isn't applied" when it actually is.
    if (process.env.NODE_ENV !== "production") return;
    // Opt-in: the /serwist/sw.js route returned HTTP 500 on the production host
    // (Lighthouse "errors in console"), so don't register unless explicitly enabled.
    if (process.env.NEXT_PUBLIC_ENABLE_SW !== "1") return;
    const t = window.setTimeout(() => setRegister(true), 2500);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <>
      {children}
      {register ? <SerwistProvider swUrl={swUrl} /> : null}
    </>
  );
}
